import {
  createRootRouteWithContext,
  Link,
  Outlet,
  useMatches,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ConsentBanner } from '@/components/ConsentBanner';
import { Footer } from '@/components/Footer';
import { GuestNav } from '@/components/GuestNav';
import { m } from '@/paraglide/messages';

// Augment TanStack Router's staticData so routes can opt out of the global chrome.
declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    chrome?: boolean;
  }
}

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RootErrorBoundary,
  notFoundComponent: NotFound,
});

function RootLayout() {
  const matches = useMatches();
  const chromeless = matches.some(
    (match) => (match.staticData as { chrome?: boolean } | undefined)?.chrome === false,
  );
  // ConsentBanner sits in BOTH branches. /login is chromeless and is a real
  // entry point — a magic link lands there directly — so a banner that only
  // rode along with the footer would miss the one page some guests see first.
  if (chromeless) {
    return (
      <div className="flex min-h-full flex-col">
        <Outlet />
        <ConsentBanner />
      </div>
    );
  }
  return (
    <div className="flex min-h-full flex-col">
      <GuestNav />
      <div className="flex flex-1 flex-col">
        <Outlet />
      </div>
      <Footer />
      <ConsentBanner />
    </div>
  );
}

function CenteredShell({
  title,
  body,
  children,
}: {
  title: string;
  body: string;
  children?: ReactNode;
}) {
  return (
    <main className="grid flex-1 place-items-center px-6 py-16">
      <section className="w-full max-w-md text-center">
        <h1 className="mb-2 text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="mb-6 text-muted">{body}</p>
        {children}
      </section>
    </main>
  );
}

function NotFound() {
  return (
    <CenteredShell title={m.not_found_title()} body={m.not_found_body()}>
      <Link
        to="/"
        className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2.5 font-medium text-primary-foreground transition hover:opacity-90"
      >
        {m.not_found_home_link()}
      </Link>
    </CenteredShell>
  );
}

function RootErrorBoundary() {
  return <CenteredShell title={m.error_title()} body={m.error_body()} />;
}
